import React from 'react';
import SatelliteIndexChart from '../../components/graphs/SatelliteIndexChart';

const SatelliteDataCharts = ({ satelliteData, selectedLayer }) => {
    if (!satelliteData || satelliteData.length === 0) return null;

    // Assuming we take the first item from the array if it's an array
    const data = Array.isArray(satelliteData) ? satelliteData[0] : satelliteData;

    const indices = [
        { key: 'ndvi', label: 'NDVI (Vegetation Index)', color: '#2FB95D' },
        { key: 'evi', label: 'EVI (Enhanced Vegetation Index)', color: '#8884d8' },
        { key: 'savi', label: 'SAVI (Soil Adjusted Vegetation Index)', color: '#ffc658' },
        { key: 'ndre', label: 'NDRE (Normalized Difference Red Edge)', color: '#ff7300' },
        { key: 'ndmi', label: 'NDMI (Normalized Difference Moisture Index)', color: '#0088FE' },
        { key: 'ndwi', label: 'NDWI (Normalized Difference Water Index)', color: '#00C49F' },
        { key: 'soc_vis', label: 'SOC VIS (Soil Organic Carbon)', color: '#d0ed57' },
        { key: 'soc_swir', label: 'SOC SWIR (Soil Organic Carbon)', color: '#a4de6c' },
        { key: 'lai', label: 'LAI (Leaf Area Index)', color: '#82ca9d' },
    ];

    const selectedIndex = indices.find(idx => idx.key === selectedLayer) || indices[0];

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-3 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800">{selectedIndex.label}</h3>
                <p className="text-xs text-gray-500">Satellite Analytics & Time Series</p>
            </div>

            {/* Metadata Section */}
            <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-100">
                <div className="flex flex-wrap gap-6">
                    <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">Last Observation</span>
                        <span className="text-sm font-medium text-gray-700">{data.last_observation || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">Polygon ID</span>
                        <span className="text-sm font-mono text-gray-600">{data.polygon_id?.slice(0, 12) || 'N/A'}...</span>
                    </div>
                </div>
            </div>

            {/* Chart Section */}
            <div className="p-4">
                {(() => {
                    const timeSeries = data[`${selectedIndex.key}_time_series`];
                    const squaredMean = data[`${selectedIndex.key}_squared_mean_data`];

                    if (!timeSeries) {
                        return (
                            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                <p className="text-sm font-medium">No time series data available</p>
                                <p className="text-xs text-gray-400 mt-1">for {selectedIndex.label}</p>
                            </div>
                        );
                    }

                    return (
                        <div className="space-y-3">
                            <div className="h-64 bg-gray-50/30 rounded-lg p-1">
                                <SatelliteIndexChart
                                    title={selectedIndex.label}
                                    data={timeSeries}
                                    color={selectedIndex.color}
                                />
                            </div>
                            {squaredMean !== undefined && (
                                <div className="flex items-center gap-3">
                                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 rounded-lg border border-blue-100">
                                        <span className="text-xs font-semibold text-gray-600">Squared Mean</span>
                                        <span className="text-sm font-mono font-bold text-blue-600">{squaredMean.toFixed(6)}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default SatelliteDataCharts;
